import { useCallback, useEffect, useState } from 'react';
import { useMsal } from 'src/auth/firebaseMsalCompat';
import axios from 'axios';

import { loginRequest } from '../../config/authConfig';
import { getApiHost } from '../../utils/getApiHost';

/** localStorage key backing the guest tutorial "seen" flag (see below). */
const GUEST_TUTORIAL_SEEN_KEY = 'guestProjectTutorialSeen';

/** Guests have no backend record, so their "seen" flag lives in localStorage.
 * Missing → `false` so the tour auto-launches once for a first-time guest; on
 * SSR / storage errors we assume `true` to avoid popping an unwanted tour. */
const readGuestTutorialSeen = (): boolean => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(GUEST_TUTORIAL_SEEN_KEY) === '1';
  } catch {
    return true;
  }
};

/**
 * Reads and mutates the "has seen the project page onboarding tutorial" flag.
 *
 * Members use the per-user backend flag (`GET /user/tutorial-status` +
 * `POST /user/tutorial-seen`), intentionally decoupled from
 * `/user/subscription-status` so the project page can read it cheaply without
 * triggering a Stripe query.
 *
 * Guests (`isGuest`) have no backend user record and MUST NOT hit those
 * endpoints — they self-provision a Stripe customer via `ensure_provisioned` —
 * so their flag is tracked client-side in localStorage instead (device-scoped).
 *
 * Failures are deliberately swallowed (never routed through an error
 * boundary): the onboarding tour is a non-critical enhancement, so a flaky
 * status read should not block — or crash — the project view. On a failed
 * read we default `hasSeenTutorial` to `true` so we DON'T pop an unwanted
 * tour at someone whose flag we couldn't fetch.
 */
export const useProjectTutorial = (
  isAuthenticated: boolean,
  isGuest = false
) => {
  const { instance, inProgress } = useMsal();
  const [hasSeenTutorial, setHasSeenTutorial] = useState<boolean | null>(null);
  const [tutorialLoading, setTutorialLoading] = useState(false);

  useEffect(() => {
    // Guests: resolve the flag from localStorage, never the backend.
    if (isGuest) {
      setHasSeenTutorial(readGuestTutorialSeen());
      return;
    }

    if (inProgress !== 'none' || !isAuthenticated) return;

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        setTutorialLoading(true);
        const authResult = await instance.acquireTokenSilent({
          ...loginRequest,
        });
        const apiUrl = getApiHost();
        const response = await axios.get(`${apiUrl}/user/tutorial-status`, {
          headers: { Authorization: `Bearer ${authResult.idToken}` },
        });
        if (!cancelled) {
          setHasSeenTutorial(response.data?.hasSeenProjectTutorial === true);
        }
      } catch (err) {
        // Non-fatal: assume "seen" so a transient failure can't surface
        // an unwanted tour. The "?" button still lets the user replay.
        console.warn('Failed to fetch tutorial status:', err);
        if (!cancelled) setHasSeenTutorial(true);
      } finally {
        if (!cancelled) setTutorialLoading(false);
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [instance, inProgress, isAuthenticated, isGuest]);

  const markSeen = useCallback(async () => {
    // Optimistically flip locally so the auto-launch guard won't re-fire
    // within this session even if the network write is slow or fails.
    setHasSeenTutorial(true);
    // Guests persist the flag in localStorage (no backend record to write to).
    if (isGuest) {
      try {
        window.localStorage.setItem(GUEST_TUTORIAL_SEEN_KEY, '1');
      } catch {
        // Storage disabled/full: harmless — the tour may re-launch next session.
      }
      return;
    }
    if (!isAuthenticated) return;
    try {
      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });
      const apiUrl = getApiHost();
      await axios.post(
        `${apiUrl}/user/tutorial-seen`,
        {},
        { headers: { Authorization: `Bearer ${authResult.idToken}` } }
      );
    } catch (err) {
      // Swallow — worst case the tour auto-launches again on the next
      // device/session, which is harmless.
      console.warn('Failed to mark tutorial as seen:', err);
    }
  }, [instance, isAuthenticated, isGuest]);

  return { hasSeenTutorial, tutorialLoading, markSeen };
};
